import { getLogger } from '../../libs/logger';
import { getIsAuthSessionTelemetryEnabled } from '../../utils/authSessionTelemetryFlag';

const logger = getLogger('authSessionTelemetry');

export const AUTH_NAVIGATION_CORRELATION_MS = 2000;

export const FRONTEGG_TRACE_ID_HEADER = 'frontegg-trace-id';

export const AuthHttpInitiator = {
  KeepSessionAlive: 'keep_session_alive',
  RequestAuthorize: 'request_authorize',
  ClientNavigation: 'client_navigation',
  PageReload: 'page_reload',
  Unknown: 'unknown',
} as const;

export type AuthHttpInitiator =
  (typeof AuthHttpInitiator)[keyof typeof AuthHttpInitiator];

export type FronteggHttpRecord = {
  urlPath: string;
  method: string;
  status: number;
  ok: boolean;
  durationMs: number;
  initiator: AuthHttpInitiator;
  errorCode?: string;
  errorMessage?: string;
  queryParamKeys?: string[];
  fronteggTraceId?: string;
};

/** Trimmed auth HTTP record stored in the session-loss sliding window. */
export type RecentFronteggHttpEntry = {
  timestamp: number;
  urlPath: string;
  method: string;
  status: number;
  ok: boolean;
  durationMs: number;
  initiator: AuthHttpInitiator;
  errorCode?: string;
  errorMessage?: string;
  fronteggTraceId?: string;
};

export type RequestAuthorizeHttpSummary = Pick<
  FronteggHttpRecord,
  | 'urlPath'
  | 'method'
  | 'status'
  | 'durationMs'
  | 'initiator'
  | 'fronteggTraceId'
  | 'errorCode'
  | 'errorMessage'
>;

export type RequestAuthorizeResult = {
  success: boolean;
  error?: NormalizedAuthError;
  completedAt: number;
  /** Present when authorize failed due to a non-2xx Frontegg auth HTTP response. */
  http?: RequestAuthorizeHttpSummary;
};

export type NormalizedAuthError = {
  name: string;
  message: string;
  status?: number;
  code?: string;
};

export type AuthSessionTelemetrySnapshot = {
  loginGenerationId: string | null;
  successfulRefreshCountSinceLogin: number;
  lastSuccessfulRefreshAt: number | null;
  inferredLossTrigger: AuthHttpInitiator;
  requestAuthorizeLastResult: RequestAuthorizeResult | null;
  lastNavigationAt: number | null;
  lastNavigationPath: string | null;
  pageLoadType: PageLoadType | null;
  recentFronteggHttp: RecentFronteggHttpEntry[];
};

export type PageLoadType = 'reload' | 'navigate' | 'back_forward' | 'prerender';

export const MAX_RECENT_FRONTEGG_HTTP = 10;

const RECENT_HTTP_ERROR_MESSAGE_MAX_LENGTH = 200;

const EMPTY_AUTH_SESSION_TELEMETRY_SNAPSHOT: AuthSessionTelemetrySnapshot = {
  loginGenerationId: null,
  successfulRefreshCountSinceLogin: 0,
  lastSuccessfulRefreshAt: null,
  inferredLossTrigger: AuthHttpInitiator.Unknown,
  requestAuthorizeLastResult: null,
  lastNavigationAt: null,
  lastNavigationPath: null,
  pageLoadType: null,
  recentFronteggHttp: [],
};

type NavigationState = {
  lastNavigationAt: number | null;
  lastNavigationPath: string | null;
  pageLoadType: PageLoadType | null;
};

let loginGenerationId: string | null = null;
let successfulRefreshCount = 0;
let lastSuccessfulRefreshAt: number | null = null;
let requestAuthorizeLastResult: RequestAuthorizeResult | null = null;
let lastSessionLossContext: AuthSessionTelemetrySnapshot | null = null;
let requestAuthorizeCorrelationDepth = 0;
let authorizeCorrelationFailedHttp: FronteggHttpRecord | null = null;
let navigationState: NavigationState = {
  lastNavigationAt: null,
  lastNavigationPath: null,
  pageLoadType: null,
};

const recentFronteggHttp: RecentFronteggHttpEntry[] = [];

const createLoginGenerationId = (): string =>
  `login_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const truncateErrorMessage = (message: string): string =>
  message.length <= RECENT_HTTP_ERROR_MESSAGE_MAX_LENGTH
    ? message
    : `${message.slice(0, RECENT_HTTP_ERROR_MESSAGE_MAX_LENGTH)}…`;

const toRecentFronteggHttpEntry = (
  record: FronteggHttpRecord,
  timestamp: number,
): RecentFronteggHttpEntry => ({
  timestamp,
  urlPath: record.urlPath,
  method: record.method,
  status: record.status,
  ok: record.ok,
  durationMs: record.durationMs,
  initiator: record.initiator,
  ...(record.errorCode !== undefined ? { errorCode: record.errorCode } : {}),
  ...(record.errorMessage !== undefined
    ? { errorMessage: truncateErrorMessage(record.errorMessage) }
    : {}),
  ...(record.fronteggTraceId !== undefined
    ? { fronteggTraceId: record.fronteggTraceId }
    : {}),
});

const pushRecentFronteggHttp = (entry: RecentFronteggHttpEntry): void => {
  recentFronteggHttp.push(entry);
  if (recentFronteggHttp.length > MAX_RECENT_FRONTEGG_HTTP) {
    recentFronteggHttp.shift();
  }
};

const isWithinNavigationWindow = (atMs: number): boolean => {
  if (navigationState.lastNavigationAt === null) {
    return false;
  }
  return (
    atMs - navigationState.lastNavigationAt <= AUTH_NAVIGATION_CORRELATION_MS
  );
};

export const classifyAuthHttpInitiator = (
  atMs: number = Date.now(),
): AuthHttpInitiator => {
  if (requestAuthorizeCorrelationDepth > 0) {
    return AuthHttpInitiator.RequestAuthorize;
  }
  if (
    navigationState.pageLoadType === 'reload' &&
    isWithinNavigationWindow(atMs)
  ) {
    return AuthHttpInitiator.PageReload;
  }
  if (isWithinNavigationWindow(atMs)) {
    return AuthHttpInitiator.ClientNavigation;
  }
  return AuthHttpInitiator.KeepSessionAlive;
};

export const inferSessionLossTrigger = (): AuthHttpInitiator => {
  const latestFailed = [...recentFronteggHttp]
    .reverse()
    .find((entry) => !entry.ok);
  if (latestFailed && latestFailed.initiator !== AuthHttpInitiator.Unknown) {
    return latestFailed.initiator;
  }

  const latest = recentFronteggHttp.at(-1);
  if (latest && latest.initiator !== AuthHttpInitiator.Unknown) {
    return latest.initiator;
  }

  if (
    navigationState.pageLoadType === 'reload' &&
    isWithinNavigationWindow(Date.now())
  ) {
    return AuthHttpInitiator.PageReload;
  }
  if (isWithinNavigationWindow(Date.now())) {
    return AuthHttpInitiator.ClientNavigation;
  }
  return AuthHttpInitiator.Unknown;
};

export const getAuthSessionTelemetrySnapshot =
  (): AuthSessionTelemetrySnapshot => {
    if (!getIsAuthSessionTelemetryEnabled()) {
      return EMPTY_AUTH_SESSION_TELEMETRY_SNAPSHOT;
    }

    return {
      loginGenerationId,
      successfulRefreshCountSinceLogin: successfulRefreshCount,
      lastSuccessfulRefreshAt,
      inferredLossTrigger: inferSessionLossTrigger(),
      requestAuthorizeLastResult,
      lastNavigationAt: navigationState.lastNavigationAt,
      lastNavigationPath: navigationState.lastNavigationPath,
      pageLoadType: navigationState.pageLoadType,
      recentFronteggHttp: [...recentFronteggHttp],
    };
  };

export const getLastSessionLossContext =
  (): AuthSessionTelemetrySnapshot | null => lastSessionLossContext;

export const recordNavigation = (
  path: string,
  atMs: number = Date.now(),
): void => {
  if (!getIsAuthSessionTelemetryEnabled()) {
    return;
  }

  navigationState = {
    ...navigationState,
    lastNavigationAt: atMs,
    lastNavigationPath: path,
  };
};

export const recordPageLoadType = (pageLoadType: PageLoadType): void => {
  if (!getIsAuthSessionTelemetryEnabled()) {
    return;
  }

  navigationState = {
    ...navigationState,
    pageLoadType,
  };
};

export const recordAuthSessionStarted = (): void => {
  if (!getIsAuthSessionTelemetryEnabled()) {
    return;
  }

  loginGenerationId = createLoginGenerationId();
  successfulRefreshCount = 0;
  lastSuccessfulRefreshAt = null;
  lastSessionLossContext = null;
  requestAuthorizeLastResult = null;
  recentFronteggHttp.length = 0;

  logger.info('auth_session_started', {
    event: 'auth_session_started',
    loginGenerationId,
    pageLoadType: navigationState.pageLoadType,
  });
};

export const recordSuccessfulTokenRefresh = (
  atMs: number = Date.now(),
): void => {
  if (!getIsAuthSessionTelemetryEnabled()) {
    return;
  }

  successfulRefreshCount += 1;
  lastSuccessfulRefreshAt = atMs;
};

export const recordAuthSessionLost = (context: {
  path?: string;
  isLoading: boolean;
  hasAccessToken: boolean;
  hasFronteggUser: boolean;
}): void => {
  if (!getIsAuthSessionTelemetryEnabled()) {
    return;
  }

  const snapshot = getAuthSessionTelemetrySnapshot();
  lastSessionLossContext = snapshot;

  const timeSinceLastNavigationMs =
    navigationState.lastNavigationAt === null
      ? null
      : Date.now() - navigationState.lastNavigationAt;

  logger.warn('auth_session_lost', {
    event: 'auth_session_lost',
    ...context,
    ...snapshot,
    visibilityState:
      typeof document !== 'undefined' ? document.visibilityState : undefined,
    timeSinceLastNavigationMs,
  });
};

const toRequestAuthorizeHttpSummary = (
  record: FronteggHttpRecord,
): RequestAuthorizeHttpSummary => ({
  urlPath: record.urlPath,
  method: record.method,
  status: record.status,
  durationMs: record.durationMs,
  initiator: record.initiator,
  fronteggTraceId: record.fronteggTraceId,
  errorCode: record.errorCode,
  errorMessage: record.errorMessage,
});

const fronteggHttpRecordToError = (
  record: FronteggHttpRecord,
): NormalizedAuthError => ({
  name: 'FronteggAuthHttpError',
  message:
    record.errorMessage ??
    (record.status > 0 ? `HTTP ${record.status}` : 'Frontegg auth HTTP failed'),
  status: record.status > 0 ? record.status : undefined,
  code: record.errorCode,
});

export const recordFronteggHttp = (record: FronteggHttpRecord): void => {
  if (!getIsAuthSessionTelemetryEnabled()) {
    return;
  }

  const completedAt = Date.now();
  pushRecentFronteggHttp(toRecentFronteggHttpEntry(record, completedAt));

  if (record.ok) {
    recordSuccessfulTokenRefresh(completedAt);
    return;
  }

  if (requestAuthorizeCorrelationDepth > 0) {
    authorizeCorrelationFailedHttp = record;
  }

  logger.warn('frontegg_auth_http', {
    event: 'frontegg_auth_http',
    ...record,
  });
};

export const normalizeAuthError = (error: unknown): NormalizedAuthError => {
  if (error instanceof Error) {
    const enriched = error as Error & { status?: number; code?: string };
    return {
      name: enriched.name,
      message: enriched.message,
      status: enriched.status,
      code: enriched.code,
    };
  }

  if (typeof error === 'object' && error !== null) {
    const record = error as Record<string, unknown>;
    return {
      name: typeof record['name'] === 'string' ? record['name'] : 'Error',
      message:
        typeof record['message'] === 'string'
          ? record['message']
          : JSON.stringify(record),
      status:
        typeof record['status'] === 'number' ? record['status'] : undefined,
      code: typeof record['code'] === 'string' ? record['code'] : undefined,
    };
  }

  return {
    name: 'Error',
    message: String(error),
  };
};

export const recordRequestAuthorizeResult = (
  success: boolean,
  error?: unknown,
  http?: FronteggHttpRecord,
): void => {
  if (!getIsAuthSessionTelemetryEnabled()) {
    return;
  }

  requestAuthorizeLastResult = {
    success,
    completedAt: Date.now(),
    error: error === undefined ? undefined : normalizeAuthError(error),
    http:
      !success && http !== undefined
        ? toRequestAuthorizeHttpSummary(http)
        : undefined,
  };
};

const finalizeRequestAuthorizeCorrelation = (
  threw: boolean,
  thrownError?: unknown,
): void => {
  if (authorizeCorrelationFailedHttp) {
    recordRequestAuthorizeResult(
      false,
      fronteggHttpRecordToError(authorizeCorrelationFailedHttp),
      authorizeCorrelationFailedHttp,
    );
    return;
  }

  if (threw) {
    recordRequestAuthorizeResult(false, thrownError);
    return;
  }

  recordRequestAuthorizeResult(true);
};

export const runWithRequestAuthorizeCorrelation = async <T>(
  fn: () => Promise<T>,
): Promise<T> => {
  if (!getIsAuthSessionTelemetryEnabled()) {
    return fn();
  }

  requestAuthorizeCorrelationDepth += 1;
  authorizeCorrelationFailedHttp = null;
  try {
    const result = await fn();
    finalizeRequestAuthorizeCorrelation(false);
    return result;
  } catch (error) {
    finalizeRequestAuthorizeCorrelation(true, error);
    throw error;
  } finally {
    requestAuthorizeCorrelationDepth -= 1;
    if (requestAuthorizeCorrelationDepth === 0) {
      authorizeCorrelationFailedHttp = null;
    }
  }
};

/** Resets module state — for tests only. */
export const resetAuthSessionTelemetry = (): void => {
  loginGenerationId = null;
  successfulRefreshCount = 0;
  lastSuccessfulRefreshAt = null;
  requestAuthorizeLastResult = null;
  lastSessionLossContext = null;
  requestAuthorizeCorrelationDepth = 0;
  authorizeCorrelationFailedHttp = null;
  navigationState = {
    lastNavigationAt: null,
    lastNavigationPath: null,
    pageLoadType: null,
  };
  recentFronteggHttp.length = 0;
};
